<template>
  <span
    v-if="span && !isDeleted"
    :id="side === 'lhs' ? span.start + 1 : span.start + 10000001"
    :key="key + isEditing"
    class="tspan"
    :class="{
      unclear: isUnclear && span.content.trim().length > 0,
      searchword: signalType === 'Search Word' && span.content.trim().length > 0,
      casekw: signalType === 'Case Keyword' && span.content.trim().length > 0,
      codeword: signalType === 'Code Word' && span.content.trim().length > 0,
      incident: hasIncident,
      'incident-start': isIncidentStart,
      'incident-end': isIncidentEnd,
      incident: hasIncident,
      redacted: hasRedaction,
      keyword: keywordPosition > -1,
      current: isCurrentKeyword,
      is: isPlaying || isFollowing,
      playing: isPlaying,
      following: isFollowing,
      insideClip: isInsideClip,
      'has-change-request': hasChangeRequest,
      'cursor-pointer': audioReady || hasChangeRequest,
      'selected': isHighlighted,
    }"
    :data-key="key"
    :data-s="span.start"
    :data-e="span.end"
    :data-d="isEditing && differs"
    :data-o="span.content"
    :data-so="span.startOffset"
    :data-eo="span.endOffset"
    :data-spk="speakerPos"
    :style="styleOverrides"
    @click.left="handleClick"
    @dblclick="handleDouble"
    @input="onInput"
    @keydown.tab="tabup"
  >
    {{ span.content }}
  </span>
</template>

<script>
import {isDefined} from "../../../../api/helpers";
export default {
  name: "transcription-span",
  props: {
    span: {
      type: Object,
      default: null,
    },
    clipRange: {
      type: Object,
      default: null,
    },
    speakerPos: {
      type: Number,
      default: -1,
    },
    incidents: {
      type: Array,
      default() {
        return [];
      },
    },
    unclearThreshold: {
      type: Number,
      default: 75,
    },
    currentPlaytime: {
      type: Number,
      default: -1,
    },
    keywordIndices: {
      type: Array,
      default() {
        return [];
      },
    },
    keywordCurrentIndex: {
      type: Number,
      default: -1,
    },
    signalMatches: {
      type: Array,
      default() {
        return [];
      },
    },
    audioReady: {
      type: Boolean,
      default: false,
    },
    hasChangeRequest: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    signalValue: {
      type: Boolean,
      default: true,
    },
    unclearValue: {
      type: Boolean,
      default: true,
    },
    side: {
      type: String,
      default: "lhs",
    },
  },
  watch: {
    isCurrentKeyword: {
      handler(to, from) {
        // if (to && !from) {
        //   const el = document.querySelector(`span[data-key="${this.key}"]`);
        //   if (el) el.scrollIntoView();
        // }
      },
      immediate: true,
    },
    isPlaying: {
      handler(to, from) {
        if (to && !from) {
          const el = document.querySelector(`span[data-key="${this.key}"]`);
          this.$emit("isPlaying", el);
        }
      },
      immediate: true,
    },
  },
  computed: {
    key() {
      return `${this.side}${this.span.start}-${this.span.end}`;
    },
    isUnclear() {
      return this.span.confidence < this.unclearThreshold && !this.unclearValue;
    },
    isFollowing() {
      return this.currentPlaytime < this.span.endOffset;
    },
    isPlaying() {
      return this.currentPlaytime >= this.span.startOffset && this.currentPlaytime < this.span.endOffset;
    },
    isInsideClip() {
      return this.clipRange != null && this.clipRange.in <= this.span.startOffset &&
       this.span.endOffset <= this.clipRange.out;
    },
    keywordPosition() {
      for (let i = 0; i < this.keywordIndices.length; i++) {
        const index = this.keywordIndices[i].offset;
        const length = this.keywordIndices[i].length;
        const side = this.keywordIndices[i].side;
        if (this.side === side && this.span.start + 1 >= index && this.span.start < index + length) return i;
      }
      return -1;
    },
    isCurrentKeyword() {
      const currentIndex = this.keywordIndices[this.keywordCurrentIndex];
      if (!currentIndex) return false;
      return this.side === currentIndex.side && this.span.start + 1 >= currentIndex.offset &&
        this.span.start < currentIndex.offset + currentIndex.length;
    },
    hasIncident() {
      return isDefined(this.incident);
    },
    isIncidentEnd() {
      return this.hasIncident && this.incident.endOffset <= this.span.end;
    },
    isIncidentStart() {
      return this.hasIncident && this.incident.startOffset >= this.span.start;
    },
    incident() {
      if (this.side !== "lhs") return null;
      return this.incidents &&
        this.incidents.find((i) => this.span.start < i.endOffset && this.span.end > i.startOffset);
    },
    signalType() {
      let sigType = "none";
      if (!this.signalValue) {
        for (const sm of this.signalMatches) {
          let done = false;
          for (let i = 0; i < sm.matches.length; i++) {
            if (sm.matches[i].side === this.side &&
              this.span.start + 1 >= sm.matches[i].index &&
              this.span.start + 1 < sm.matches[i].index + sm.matches[i].length) {
              done = true;
              if (sm.groupName === "Search Word" || sm.groupName === "Case Keyword") {
                sigType = sm.groupName;
              } else {
                sigType = "Code Word";
              }
              break;
            }
          }
          if (done) break;
        }
      }
      return sigType;
    },
    hasRedaction() {
      return this.span.redacted;
    },
    styleOverrides() {
      if (!this.hasIncident) return "";
      if (!isDefined(this.incident.color)) return "cursor:pointer;";
      return `cursor:pointer;background-color:${this.incident.color}33;color:${this.incident.color};`;
    },
  },
  data() {
    return {
      differs: false,
      isDeleted: false,
      isHighlighted: false,
    };
  },
  methods: {
    onInput(data) {
      const eTrimmed = data.trim();
      const spanTrimmed = this.span.content.trim();
      this.differs = eTrimmed !== spanTrimmed;
    },
    handleDouble(e) {
      this.$emit("seek", this.span.startOffset);
    },
    handleClick(e) {
      if (this.keywordPosition > -1) {
        this.$emit("index", this.keywordPosition);
      }
    },
    tabup(e) {
      const isForwards = !e.shiftKey;
      const x = document.querySelector(`span[data-key="${this.key}"]`);
      if (isForwards && x.parentNode.lastChild === x) {
        this.$emit("nextSpeaker");
        e.preventDefault();
      } else if (!isForwards && x.parentNode.firstChild === x) {
        this.$emit("prevSpeaker");
        e.preventDefault();
      }
    },
    delete() {
      this.isDeleted = true;
    },
  },
};
</script>

<style>
.audio-transcript .span-container.has-span {
  /* Could turn this on */
  /* background: #333; */
}

.audio-transcript .span-container span.is.playing {
  color: white;
  background: unset;
}

.audio-transcript .span-container span:not(.is.following) + .is.following {
  color: #000 !important;
  background: #fff !important;
  border-radius: 0 !important;
}

.audio-transcript .span-container.has-span span.is.following {
  background: #404048;
}
.audio-transcript .span-container.has-span.highlight-edit-section span.is.following {
  background: #f0f0f0;
}
span[data-new-speaker-s], span[data-new-speaker-e] {
  position: relative;
}

span[data-new-speaker-s]::before, span[data-new-speaker-e]::after {
    content: '"';
    background: #044f4f;
    position: absolute;
    color: white;
    padding: 0.5em 0.2em 0 0.2em;
    font-weight: bold;
    line-height: 0.25em;
    margin: 0;
    border-radius: 1em;
}
span[data-new-speaker-s]::before {
    left: -0.5em;
    top: -0.6em;
}
span[data-new-speaker-e]::after {
    right: -0.25em;
    bottom: 1em;
}
</style>
